import React from "react";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Divider from "@mui/material/Divider";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import {
  useGetNotificationsQuery,
  useMarkNotificationReadMutation,
  useMarkAllNotificationsReadMutation,
} from "../../Api/attoDeskApi";
import { INotification } from "../../Api/Interface/api.interface";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

dayjs.extend(relativeTime);

interface IProfileProps {
  notification: HTMLElement | null;
  onClose: () => void;
  open: boolean;
}

const TYPE_COLOR: Record<string, string> = {
  info:    "#0ea5e9",
  success: "#16a34a",
  warning: "#f59e0b",
  error:   "#ef4444",
};

const Notification: React.FC<IProfileProps> = ({ notification, onClose, open }) => {
  const { data, isLoading } = useGetNotificationsQuery(undefined, { skip: !open });
  const [markRead]    = useMarkNotificationReadMutation();
  const [markAllRead] = useMarkAllNotificationsReadMutation();

  const notifications: INotification[] = Array.isArray(data?.data) ? (data!.data as INotification[]) : [];
  const unreadCount: number = (data as any)?.unread_count ?? 0;

  const handleClickItem = async (item: INotification) => {
    if (!item.is_read) {
      await markRead(String(item.id));
    }
    onClose();
  };

  const handleMarkAllRead = async () => {
    await markAllRead();
  };

  return (
    <Menu
      anchorEl={notification}
      id="notification-menu"
      open={open}
      onClose={onClose}
      PaperProps={{
        elevation: 0,
        sx: {
          borderRadius: 4,
          overflow: "visible",
          filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
          mt: 1.5,
          minWidth: 340,
          maxHeight: 460,
          overflowY: "auto",
          "&::before": {
            content: '""',
            display: "block",
            position: "absolute",
            top: 0,
            right: 14,
            width: 10,
            height: 10,
            bgcolor: "background.paper",
            transform: "translateY(-50%) rotate(45deg)",
            zIndex: 0,
          },
        },
      }}
      transformOrigin={{ horizontal: "right", vertical: "top" }}
      anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
    >
      {/* Header */}
      <Box sx={{ px: 2, py: 1.5, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Typography sx={{ fontWeight: 700, fontSize: 14, color: "#0f172a" }}>
          Notifications {unreadCount > 0 && (
            <Box component="span" sx={{ ml: 0.75, px: 0.75, py: 0.1, bgcolor: "#ef4444", color: "#fff", borderRadius: 10, fontSize: 11, fontWeight: 700 }}>
              {unreadCount}
            </Box>
          )}
        </Typography>
        {unreadCount > 0 && (
          <Button size="small" onClick={handleMarkAllRead}
            sx={{ fontSize: 11, textTransform: "none", color: "#0ea5e9", p: 0, minWidth: 0 }}>
            Mark all read
          </Button>
        )}
      </Box>

      <Divider />

      {/* Body */}
      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
          <CircularProgress size={24} />
        </Box>
      ) : notifications.length === 0 ? (
        <Box sx={{ py: 4, textAlign: "center" }}>
          <Typography sx={{ fontSize: 13, color: "#94a3b8" }}>No notifications yet</Typography>
        </Box>
      ) : (
        notifications.map((item, idx) => (
          <React.Fragment key={item.id}>
            <MenuItem
              onClick={() => handleClickItem(item)}
              sx={{
                alignItems: "flex-start",
                gap: 1.5,
                py: 1.25,
                px: 2,
                bgcolor: item.is_read ? "transparent" : "rgba(14,165,233,0.05)",
                "&:hover": { bgcolor: "rgba(14,165,233,0.08)" },
              }}
            >
              {/* Type dot */}
              <Box sx={{
                width: 8, height: 8, borderRadius: "50%", mt: 0.6, flexShrink: 0,
                bgcolor: item.is_read ? "#cbd5e1" : (TYPE_COLOR[item.type] ?? "#0ea5e9"),
              }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 13, fontWeight: item.is_read ? 400 : 600, color: "#1e293b", lineHeight: 1.4 }}>
                  {item.title}
                </Typography>
                {item.message && (
                  <Typography sx={{ fontSize: 12, color: "#64748b", mt: 0.25, lineHeight: 1.4,
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {item.message}
                  </Typography>
                )}
                <Typography sx={{ fontSize: 11, color: "#94a3b8", mt: 0.5 }}>
                  {dayjs(item.created_at).fromNow()}
                </Typography>
              </Box>
            </MenuItem>
            {idx < notifications.length - 1 && <Divider sx={{ my: 0 }} />}
          </React.Fragment>
        ))
      )}
    </Menu>
  );
};

export default Notification;
